import React from 'react';
import {
  Box, Typography, Paper, Button, Avatar, Grid,
} from '@mui/material';
import { Logout, MenuBook, CheckCircle, Bookmark, Star } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useRecipes } from '../../contexts/RecipeContext';

function StatItem({ icon, label, value, color }) {
  return (
    <Paper sx={{ p: 2.5, textAlign: 'center' }}>
      <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
      <Typography variant="h5" fontWeight={800} sx={{ color }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const { recipes } = useRecipes();

  const madeCount = recipes.filter((r) => r.status === 'made').length;
  const bookmarkedCount = recipes.filter((r) => r.status === 'bookmarked').length;
  const starredCount = recipes.filter((r) => r.starred).length;

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" sx={{ mb: 3 }}>Settings</Typography>

      {/* Account */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Account</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar
            sx={{
              width: 56, height: 56,
              background: 'linear-gradient(135deg, #f0873c, #c96820)',
              fontSize: '1.5rem', fontWeight: 700,
            }}
          >
            {user?.email?.[0]?.toUpperCase() || '?'}
          </Avatar>
          <Box>
            <Typography fontWeight={600}>{user?.email}</Typography>
            <Typography variant="body2" color="text.secondary">
              Member since {user?.metadata?.creationTime
                ? new Date(user.metadata.creationTime).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                : '—'}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Logout />}
          onClick={logout}
          color="error"
          sx={{ borderColor: 'rgba(239,83,80,0.4)' }}
        >
          Sign Out
        </Button>
      </Paper>

      {/* Stats */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Your Stats</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <StatItem icon={<MenuBook />} label="Total Recipes" value={recipes.length} color="primary.main" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatItem icon={<CheckCircle />} label="Made" value={madeCount} color="success.main" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatItem icon={<Bookmark />} label="Bookmarked" value={bookmarkedCount} color="warning.main" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatItem icon={<Star />} label="Starred" value={starredCount} color="#ffa726" />
          </Grid>
        </Grid>
      </Paper>

      {/* Firebase note */}
      <Paper sx={{ p: 3, bgcolor: 'rgba(240,135,60,0.05)', borderColor: 'rgba(240,135,60,0.15)' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Note: Firebase Indexes</Typography>
        <Typography variant="body2" color="text.secondary">
          If recipes fail to load, you may need to create a composite index in Firebase Console:
          Collection <strong>recipes</strong> → fields <strong>userId (asc)</strong> + <strong>createdAt (desc)</strong>.
          Firebase will show a link in the console error message to create it automatically.
        </Typography>
      </Paper>
    </Box>
  );
}
